import React, { useLayoutEffect, useEffect } from "react";
import posthog from "posthog-js";
import { Toaster } from "react-hot-toast";
import { QueryClientProvider } from "react-query";
import { tgQueryClient as queryClient } from "./utils/tgQueryClient";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
// Landing site v2. One flag in src/landing/landing.config.js decides whether the
// new marketing site serves the public pages or the routes below do.
import { LandingSwitch } from "./landing";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { ConfigProvider, theme as antdTheme, Skeleton } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import {
  pageVariants,
  pageTransition,
  fadeInVariants,
} from "./utils/animations";
import LayoutSkeleton from "./components/Layout/LayoutSkeleton";

// Context
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { GoalsSettingsProvider } from "./contexts/GoalsSettingsContext";

// Components
import Layout from "./components/Layout/Layout";
import WhatsAppButton from "./components/WhatsAppButton/WhatsAppButton";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import VerifyEmail from "./pages/Auth/VerifyEmail";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import Dashboard from "./pages/Dashboard/Dashboard";
import DashboardV2 from "./pages/DashboardV2/DashboardV2";
import { PersonDrilldown, ProjectDrilldown, ToolDrilldown, BriefDrilldown } from "./pages/DashboardV2/screens/drilldown";
import GraphicalDashboard from "./pages/DashboardV2/screens/GraphicalDashboard";
import { readDashView } from "./pages/DashboardV2/components/DashViewSwitch";
import DayDrilldown from "./pages/DashboardV2/screens/graphical/DayDrilldown";
import OrgToday from "./pages/DashboardV2/screens/graphical/OrgToday";
import GraphicalAlerts from "./pages/DashboardV2/screens/graphical/drill/GraphicalAlerts";
import BrainV2 from "./pages/DashboardV2/screens/brain/BrainV2";
import ManageProjectsV2 from "./pages/DashboardV2/screens/ManageProjectsV2";
import ProjectDetailV2 from "./pages/DashboardV2/screens/ProjectDetailV2";
import WorkSummaryV2 from "./pages/DashboardV2/screens/WorkSummaryV2";
import OrgIntelligenceV2 from "./pages/DashboardV2/screens/OrgIntelligenceV2";
import AppUsageV2 from "./pages/DashboardV2/screens/AppUsageV2";
import SkillProfilesV2 from "./pages/DashboardV2/screens/SkillProfilesV2";
import LivingToolV2 from "./pages/DashboardV2/screens/LivingToolV2";
import WorkIntelligenceV2 from "./pages/DashboardV2/screens/WorkIntelligenceV2";
import CompanyBrainV2 from "./pages/DashboardV2/screens/CompanyBrainV2";
// ADDITIVE: new two-source Work Summary. The existing WorkSummaryV2 route below is untouched.
import WorkSummaryNewV2 from "./pages/DashboardV2/screens/WorkSummaryNewV2";
// ADDITIVE: two-source Org Intelligence. CompanyBrainV2 above is untouched.
import OrgIntelligenceV2New from "./pages/DashboardV2/screens/OrgIntelligenceV2New";
import OrgIntelligenceV3 from "./pages/DashboardV2/screens/OrgIntelligenceV3"; // 2026-08-30: blockers-first rebuild; v2 screen kept at /org-intelligence-v2-legacy
import AskTgNewV2 from "./pages/DashboardV2/screens/AskTgNewV2";

import TeamV2 from "./pages/DashboardV2/screens/TeamV2";
import OrgChartV2 from "./pages/DashboardV2/screens/OrgChartV2";
import TeamAppsV2 from "./pages/DashboardV2/screens/TeamAppsV2";
import AppConnectorsV2 from "./pages/DashboardV2/screens/AppConnectorsV2";
import GoalsV2 from "./pages/DashboardV2/screens/GoalsV2";
import SettingsV2 from "./pages/DashboardV2/screens/SettingsV2";
import GoalsSettingsV2 from "./pages/DashboardV2/screens/GoalsSettingsV2";
import GoalsProjectsV2 from "./pages/DashboardV2/screens/GoalsProjectsV2";
import LayoutV1 from "./pages/DashboardV2/LayoutV1";
import { NEW_UI } from "./config/featureFlags";
import HrmsOverview from "./pages/DashboardV2/screens/hrms/HrmsOverview";
import HrmsMe from "./pages/DashboardV2/screens/hrms/HrmsMe";
import HrmsPeople from "./pages/DashboardV2/screens/hrms/HrmsPeople";
import HrmsAttendance from "./pages/DashboardV2/screens/hrms/HrmsAttendance";
import HrmsLeave from "./pages/DashboardV2/screens/hrms/HrmsLeave";
import HrmsPayroll from "./pages/DashboardV2/screens/hrms/HrmsPayroll";
import HrmsLifecycle from "./pages/DashboardV2/screens/hrms/HrmsLifecycle";
import HrmsWorkplace from "./pages/DashboardV2/screens/hrms/HrmsWorkplace";
import HrmsSettings from "./pages/DashboardV2/screens/hrms/HrmsSettings";
import InboxV2 from "./pages/DashboardV2/screens/InboxV2";

import DailySummary from "./pages/DailySummary/DailySummary";
// import MindOps from './pages/MindOps/MindOps';
import BrainOps from "./pages/BrainOps/BrainOps";
import MonthlySummary from "./pages/MonthlySummary/MonthlySummary";
import Projects from "./pages/Projects/Projects";
import ProjectDetail from "./pages/Projects/ProjectDetail";
import Reports from "./pages/Reports/Reports";
import Settings from "./pages/Settings/Settings";
import Team from "./pages/Team/Team";
import MyGoals from "./pages/Goals/MyGoals";
import MyTasks from "./pages/Goals/MyTasks";
import TasksRoute from "./pages/Tasks/TasksRoute"; // ADDITIVE: Tasks module (org-gated) or legacy placeholder
import ChatWithUs from "./pages/ChatWithUs/ChatWithUs";
import AISearch from "./pages/AISearch/AISearch";
import OrganizationProfileGuard from "./components/OrganizationProfileGuard";

import DesktopSimulator from "./pages/DesktopSimulator/DesktopSimulator";
import DownloadPage from "./pages/Download/Download";
import DownloadAllPage from "./pages/DownloadAll/DownloadAll";
import Invite from "./pages/Invite/Invite";
import Landing from "./pages/Landing/Landing";
import MarketingPromo from "./pages/Landing/market-promo/marketing-promo";
import AlternateLanding from "./pages/Landing/AlternateLanding";
import MainAdmin from "./pages/MainAdmin/MainAdmin";
import MainAdminSettings from "./pages/MainAdmin/MainAdminSettings";
import CouponManagement from "./pages/MainAdmin/CouponManagement";
import RazorpayCouponManagement from "./pages/MainAdmin/RazorpayCouponManagement";
import RazorpayLinkGenerator from "./pages/MainAdmin/RazorpayLinkGenerator";
import RevenueDashboard from "./pages/MainAdmin/RevenueDashboard";
import OrgPattern from "./pages/MainAdmin/OrgPattern";
import WeeklyReports from "./pages/MainAdmin/WeeklyReports";
import AdminSuggestions from "./pages/MainAdmin/AdminSuggestions";
import AdminAnnouncements from "./pages/MainAdmin/AdminAnnouncements";
// B2B Partner Portal (isolated)
import PartnerLogin from "./pages/PartnerLogin/PartnerLogin";
import PartnerPortal from "./pages/PartnerPortal/PartnerPortal";
import ErrorLogs from "./pages/ErrorLogs/ErrorLogs";
import Billing from "./pages/Billing/Billing";
import Privacy from "./pages/Privacy/Privacy";
import Terms from "./pages/Terms/Terms";
import Security from "./pages/Security/Security";
import BookDemo from "./pages/BookDemo/BookDemo";
import BookDemoConfirm from "./pages/BookDemoConfirm/BookDemoConfirm";
import Contact from "./pages/Contact/Contact";
import ThankYou from "./pages/ThankYou/ThankYou";
import ThanksDemoRequest from "./pages/ThanksDemoRequest/ThanksDemoRequest";
import Features from "./pages/Features/Features";
import Blog from "./pages/Blog/Blog";
import BlogDetail from "./pages/Blog/BlogDetail";
import AutomaticTimeTracking from "./pages/AutomaticTimeTracking/AutomaticTimeTracking";
import ProductivityScoring from "./pages/ProductivityScoring/ProductivityScoring";
import AIWorkSummaries from "./pages/AIWorkSummaries/AIWorkSummaries";
import AppWebsiteTracking from "./pages/AppWebsiteTracking/AppWebsiteTracking";
import HourlyBreakdown from "./pages/HourlyBreakdown/HourlyBreakdown";
import TeamManagement from "./pages/TeamManagement/TeamManagement";
import ProjectTracking from "./pages/ProjectTracking/ProjectTracking";
import OrgIntelligence from "./pages/OrgIntelligence/OrgIntelligence";
import AskTeamGrid from "./pages/Features/AskTeamGrid";
import RemoteDistributedTeams from "./pages/Solutions/RemoteDistributedTeams";
import HRStaffingFirms from "./pages/Solutions/HRStaffingFirms";
import EdTechTeams from "./pages/Solutions/EdTechTeams";
import AccountingFirms from "./pages/Solutions/AccountingFirms";
import ProjectBasedTeams from "./pages/Solutions/ProjectBasedTeams";
import VirtualSupportServices from "./pages/Solutions/VirtualSupportServices";
import BPOSharedServices from "./pages/Solutions/BPOSharedServices";
import ContractFreelanceTeams from "./pages/Solutions/ContractFreelanceTeams";
import ClientDrivenWorkTeams from "./pages/Solutions/ClientDrivenWorkTeams";
import OperationsAndExecutions from "./pages/Solutions/OperationsAndExecutions";
import WorkforcePlatformComparison from "./pages/ComparisonPages/WorkforcePlatformComparison";
import We360Comparison from "./pages/ComparisonPages/We360Comparison";
import InsightfulComparison from "./pages/ComparisonPages/InsightfulComparison";
import ProHanceComparison from "./pages/ComparisonPages/ProHanceComparison";
import TimeDoctorComparison from "./pages/ComparisonPages/TimeDoctorComparison";
import HubstaffComparison from "./pages/ComparisonPages/HubstaffComparison";
import ActivTrakComparison from "./pages/ComparisonPages/ActivTrakComparison";
import { EmailInsightsModule } from "./modules/email-insights";
import { CrmPreview, CrmGlobalProviders } from "./modules/crm";
// ADDITIVE: isolated Stealth-Agent module (main_admin only). No existing code touched.
import { StealthShell, StealthOrgIntelligence, StealthWorkSummary, StealthDevices } from "./modules/stealth-agent";
import { isStealthIntelligenceEnabledForOrg, isStealthUnderCrmOrg } from "./modules/stealth-agent/orgGating";
// ADDITIVE: isolated Employee Activity module (org-admin facing; org.employeeActivityEnabled).
import { EmployeeActivity } from "./modules/employee-activity";
// ADDITIVE: isolated Stealth Clock module (org-admin timeline; org.stealthClockEnabled).
import { ShiftTimeline } from "./modules/stealth-clock";
import {
  ResourceOptimizerPage,
  ResourceOptimizerBookPage,
} from "./modules/resource-optimizer";
import GoogleCallback from "./pages/GoogleCallback/GoogleCallback";
import TimeTrackingSoftware from "./pages/Landing/time-tracking-software/time-tracking-software";

// DEV ONLY: fixture-backed visual preview of the Tasks module (no login/backend).
const TasksPreview = process.env.NODE_ENV === "development" ? require("./modules/tasks/devPreview/PreviewApp").default : null;


// 2026-08-30 — /dashboard opens the view the user last chose (Graphical by
// default; Brain once they switched). Additive: /dashboard/graphical and
// /dashboard/brain keep working as direct links.
function DashboardHome() {
  const [pref] = React.useState(() => readDashView());
  if (pref === "brain") return <Navigate to="/dashboard/brain" replace />;
  return <GraphicalDashboard />;
}
// Wrapper so alternate landing "Main view" button navigates to /
const AlternateLandingRoute = () => {
  const navigate = useNavigate();
  return <AlternateLanding onSwitchBack={() => navigate("/")} />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // If user exists, render children
  if (user) {
    // B2B partners are isolated to the partner portal — never the app shell.
    if (user.role === "partner") {
      return <Navigate to="/partner" replace />;
    }
    return children;
  }

  // If loading is complete and no user, redirect to login
  if (!loading && !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-white">
      <div className="w-10 h-10 rounded-full border-[3px] border-gray-200 border-t-[#00D1C1] animate-spin" />
      <p className="text-[15px] font-medium text-gray-800">Signing you in…</p>
    </div>
  );
};

// Public Route Component (redirects to appropriate dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    // Commented out skeleton to prevent it from showing when user logs in
    // The auth check is very fast, so no need to show a skeleton
    // return (
    //   <motion.div
    //     variants={fadeInVariants}
    //     initial="hidden"
    //     animate="visible"
    //     className="min-h-screen flex items-center justify-center"
    //   >
    //     <Skeleton active paragraph={{ rows: 4 }} />
    //   </motion.div>
    // );
    return null;
  }

  if (user) {
    // Redirect main admin to main admin panel, partners to their portal,
    // and everyone else to the dashboard.
    const redirectPath =
      user.role === "main_admin"
        ? "/main-admin"
        : user.role === "partner"
        ? "/partner"
        : "/dashboard";
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

const SmartWorkSummaryRoute = () => {
  const { user } = useAuth();
  if (isStealthIntelligenceEnabledForOrg(user?.organizationId) && !isStealthUnderCrmOrg(user?.organizationId)) {
    return <StealthWorkSummary />;
  }
  return <WorkSummaryV2 />;
};

const SmartOrgIntelligenceRoute = () => {
  const { user } = useAuth();
  if (isStealthIntelligenceEnabledForOrg(user?.organizationId) && !isStealthUnderCrmOrg(user?.organizationId)) {
    return <StealthOrgIntelligence />;
  }
  return <OrgIntelligenceV2 />;
};

// Initialize Stripe
const stripePromise =
  (
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY &&
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY !==
      "your_stripe_publishable_key_here"
  ) ?
    loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

/** Tracks a PostHog pageview on every route change. */
const PostHogPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [location.pathname, location.search]);
  return null;
};

/** Scroll to top on every route change (e.g. dropdown links, nav links). */
const ScrollToTop = () => {
  const location = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Run again after paint so new page content doesn't override scroll (e.g. after mount)
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
    return () => cancelAnimationFrame(id);
  }, [location.pathname, location.search, location.hash]);
  return null;
};

const AppContent = () => {
  const { mode } = useTheme();
  const isDark = mode === "dark";
  const location = useLocation();

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isValidGoogleClientId =
    googleClientId && googleClientId !== "your_google_client_id_here";

  // Floating WhatsApp button shows on all PUBLIC marketing pages, but NOT on
  // auth pages (login/register/forgot/reset/verify/invite) or any authenticated
  // / inner app pages (dashboard, main-admin, billing).
  const pathname = location.pathname;
  const WHATSAPP_HIDDEN_PREFIXES = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/invite",
    "/dashboard",
    "/main-admin",
    "/billing",
    "/time-tracking-software",
    "/partner-login",
    "/partner",
  ];
  const showWhatsApp = !WHATSAPP_HIDDEN_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );

  return (
    <>
      <ScrollToTop />
      <PostHogPageTracker />
      <GoogleOAuthProvider
        clientId={isValidGoogleClientId ? googleClientId : "dummy-client-id"}
      >
        <ConfigProvider
          theme={{
            algorithm:
              isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
            token: {
              colorPrimary: "#3b82f6",
              borderRadius: 8,
              borderRadiusLG: 12,
              fontSize: 14,
              fontFamily:
                '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              colorBgBase: isDark ? "#141414" : "#ffffff",
              colorTextBase: isDark ? "#f8fafc" : "#0f172a",
              colorBorder: isDark ? "#303030" : "#f0f0f0",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
              motion: {
                duration: 0.3,
              },
            },
            components: {
              Card: {
                borderRadius: 8,
                paddingLG: 24,
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
              },
              Table: {
                borderRadius: 8,
                headerBg: isDark ? "#1f1f1f" : "#fafafa",
              },
              Button: {
                borderRadius: 8,
                controlHeight: 36,
              },
              Input: {
                borderRadius: 8,
                controlHeight: 36,
              },
              Select: {
                borderRadius: 8,
                controlHeight: 36,
              },
              Modal: {
                borderRadius: 12,
              },
              Drawer: {
                borderRadius: 12,
              },
            },
          }}
        >
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <GoalsSettingsProvider>
                <CrmGlobalProviders>
                  <Elements stripe={stripePromise}>
                    <div className="App min-h-screen bg-app-base text-app-primary transition-colors duration-300">
                      <Toaster
                        position="top-right"
                        containerStyle={{ zIndex: 100000 }}
                        toastOptions={{
                          duration: 4000,
                          style: {
                            background: isDark ? "#1f2937" : "#ffffff",
                            color: isDark ? "#f8fafc" : "#0f172a",
                            border: `1px solid ${
                              isDark ?
                                "rgba(148, 163, 184, 0.25)"
                              : "rgba(15, 23, 42, 0.08)"
                            }`,
                          },
                          success: {
                            duration: 3000,
                            iconTheme: {
                              primary: "#10B981",
                              secondary: isDark ? "#1f2937" : "#ffffff",
                            },
                          },
                          error: {
                            duration: 5000,
                            iconTheme: {
                              primary: "#EF4444",
                              secondary: isDark ? "#1f2937" : "#ffffff",
                            },
                          },
                        }}
                      />

                      <AnimatePresence mode="wait">
                        <LandingSwitch>
                        <Routes location={location}>
                          {/* SEO: redirect duplicate URLs to canonical homepage */}
                          <Route
                            path="/home"
                            element={<Navigate to="/" replace />}
                          />
                          <Route
                            path="/index"
                            element={<Navigate to="/" replace />}
                          />
                          <Route
                            path="/landing"
                            element={<Navigate to="/" replace />}
                          />
                          <Route
                            path="/index.html"
                            element={<Navigate to="/" replace />}
                          />
                          {/* Google OAuth callback for Email Insights */}
                          <Route
                            path="/google/callback"
                            element={<GoogleCallback />}
                          />

                          {/* Public Routes */}
                          {TasksPreview && <Route path="/tasks-preview/*" element={<TasksPreview />} />}
                          <Route path="/" element={<Landing />} />
                          <Route
                            path="/employee-monitoring-software"
                            element={<MarketingPromo />}
                          />
                          <Route
                            path="/time-tracking-software"
                            element={<TimeTrackingSoftware />}
                          />
                          <Route
                            path="/landing-alt"
                            element={<AlternateLandingRoute />}
                          />
                          <Route
                            path="/alternate"
                            element={<AlternateLandingRoute />}
                          />
                          <Route
                            path="/ai-workforce-intelligence"
                            element={<AlternateLandingRoute />}
                          />
                          <Route
                            path="/compare/teamgrid-ai-vs-employee-monitoring-software"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <WorkforcePlatformComparison />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/comparison/workforce-platform"
                            element={
                              <Navigate
                                to="/compare/teamgrid-ai-vs-employee-monitoring-software"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/compare/teamgrid-ai-vs-we360-ai"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <We360Comparison />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/comparison/we360"
                            element={
                              <Navigate
                                to="/compare/teamgrid-ai-vs-we360-ai"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/compare/teamgrid-ai-vs-insightful"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <InsightfulComparison />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/comparison/insightful"
                            element={
                              <Navigate
                                to="/compare/teamgrid-ai-vs-insightful"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/compare/teamgrid-ai-vs-prohance"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ProHanceComparison />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/comparison/prohance"
                            element={
                              <Navigate
                                to="/compare/teamgrid-ai-vs-prohance"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/compare/teamgrid-ai-vs-time-doctor"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <TimeDoctorComparison />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/comparison/time-doctor"
                            element={
                              <Navigate
                                to="/compare/teamgrid-ai-vs-time-doctor"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/compare/teamgrid-ai-vs-hubstaff"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <HubstaffComparison />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/comparison/hubstaff"
                            element={
                              <Navigate
                                to="/compare/teamgrid-ai-vs-hubstaff"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/compare/teamgrid-ai-vs-activtrak"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ActivTrakComparison />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/comparison/activtrak"
                            element={
                              <Navigate
                                to="/compare/teamgrid-ai-vs-activtrak"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/login"
                            element={
                              <PublicRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <Login />
                                </motion.div>
                              </PublicRoute>
                            }
                          />
                          {/* B2B Partner Portal — isolated routes */}
                          <Route
                            path="/partner-login"
                            element={<PartnerLogin />}
                          />
                          <Route
                            path="/partner"
                            element={<PartnerPortal />}
                          />
                          <Route
                            path="/main-admin"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <MainAdmin />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/settings"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <MainAdminSettings />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/error-logs"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <ErrorLogs />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/revenue"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <RevenueDashboard />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          {/* ADDITIVE: per-org user behaviour pattern (main_admin). Two views:
                              the league table, and one org's full pattern. */}
                          <Route
                            path="/main-admin/org-pattern"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <OrgPattern />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/org-pattern/:orgId"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <OrgPattern />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/weekly-reports"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <WeeklyReports />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/suggestions"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <AdminSuggestions />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/announcements"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <AdminAnnouncements />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/coupons"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <CouponManagement />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/razorpay-coupons"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <RazorpayCouponManagement />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/main-admin/razorpay-links"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <RazorpayLinkGenerator />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/billing"
                            element={
                              <ProtectedRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <Billing />
                                </motion.div>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/register"
                            element={
                              <PublicRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <Register />
                                </motion.div>
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/verify-email"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <VerifyEmail />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/forgot-password"
                            element={
                              <PublicRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <ForgotPassword />
                                </motion.div>
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/reset-password/:token"
                            element={
                              <PublicRoute>
                                <motion.div
                                  variants={pageVariants}
                                  initial="initial"
                                  animate="animate"
                                  exit="exit"
                                  transition={pageTransition}
                                >
                                  <ResetPassword />
                                </motion.div>
                              </PublicRoute>
                            }
                          />
                          <Route
                            path="/invite"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <Invite />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/privacy-policy"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <Privacy />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/terms-of-service"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <Terms />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/security"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <Security />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/download"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <DownloadPage />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/download/all"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <DownloadAllPage />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/resource-optimizer"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ResourceOptimizerPage />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/resource-optimizer/book"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ResourceOptimizerBookPage />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/book-demo"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <BookDemo />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/book-demo/confirm"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <BookDemoConfirm />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/contact-us"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <Contact />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/thanks-contact-us"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ThankYou />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/thanks-demo-request"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ThanksDemoRequest />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <Features />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/ai-powered-insights"
                            element={
                              <Navigate to="/features/ask-teamgrid" replace />
                            }
                          />
                          <Route
                            path="/features/automatic-time-tracking"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <AutomaticTimeTracking />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/productivity-scoring"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ProductivityScoring />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/ai-work-summaries"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <AIWorkSummaries />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/app-website-tracking"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <AppWebsiteTracking />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/hourly-breakdown"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <HourlyBreakdown />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/team-management"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <TeamManagement />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/performance-metrics"
                            element={
                              <Navigate
                                to="/features/productivity-scoring"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/features/project-tracking"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ProjectTracking />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/org-intelligence"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <OrgIntelligence />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/features/ask-teamgrid"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <AskTeamGrid />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/remote-team-productivity"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <RemoteDistributedTeams />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/remote-distributed-teams"
                            element={
                              <Navigate
                                to="/solutions/remote-team-productivity"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/solutions/hr-staffing-firms"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <HRStaffingFirms />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/edtech-productivity-software"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <EdTechTeams />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/edtech-teams"
                            element={
                              <Navigate
                                to="/solutions/edtech-productivity-software"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/solutions/accounting-firm-productivity"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <AccountingFirms />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/accounting-firms"
                            element={
                              <Navigate
                                to="/solutions/accounting-firm-productivity"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/solutions/project-based-teams"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ProjectBasedTeams />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/virtual-support-services"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <VirtualSupportServices />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/operations-execution-teams"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <OperationsAndExecutions />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/operations-and-executions"
                            element={
                              <Navigate
                                to="/solutions/operations-execution-teams"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/solutions/bpo-shared-services"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <BPOSharedServices />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/contract-freelance-teams"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ContractFreelanceTeams />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/client-driven-work"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <ClientDrivenWorkTeams />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/solutions/client-driven-work-teams"
                            element={
                              <Navigate
                                to="/solutions/client-driven-work"
                                replace
                              />
                            }
                          />
                          <Route
                            path="/pricing"
                            element={<Navigate to="/#pricing" replace />}
                          />
                          <Route
                            path="/blog"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <Blog />
                              </motion.div>
                            }
                          />
                          <Route
                            path="/blog/:slug"
                            element={
                              <motion.div
                                variants={pageVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                                transition={pageTransition}
                              >
                                <BlogDetail />
                              </motion.div>
                            }
                          />

                          {/* ── Dashboard routing, controlled by the NEW_UI flag ──
                              NEW_UI = false → legacy UI at /dashboard, V2 at /dashboard/v1 (original behaviour).
                              NEW_UI = true  → V2 UI mounted AT /dashboard (same URLs); legacy hidden. */}
                          {!NEW_UI && (
                            <Route
                              path="/dashboard"
                              element={
                                <ProtectedRoute>
                                  <OrganizationProfileGuard>
                                    <Layout />
                                  </OrganizationProfileGuard>
                                </ProtectedRoute>
                              }
                            >
                              <Route index element={<Dashboard />} />
                              <Route path="v2" element={<DashboardV2 />} />
                              <Route path="projects/v2" element={<ManageProjectsV2 />} />
                              <Route path="daily-summary/v2" element={<WorkSummaryV2 />} />
                              <Route path="brainops/v2" element={<OrgIntelligenceV2 />} />
                              <Route path="team/v2" element={<TeamV2 />} />
                              <Route path="projects" element={<Projects />} />
                              <Route
                                path="projects/:projectId"
                                element={<ProjectDetail />}
                              />
                              <Route path="team" element={<Team />} />
                              <Route path="activity" element={<Navigate to=".." replace />} />
                              <Route
                                path="desktop-simulator"
                                element={<DesktopSimulator />}
                              />
                              <Route path="reports" element={<Reports />} />
                              <Route path="goals" element={<MyGoals />} />
                              <Route path="tasks/*" element={<TasksRoute />} />
                              <Route
                                path="daily-summary"
                                element={<DailySummary />}
                              />
                              <Route path="brainops" element={<BrainOps />} />
                              {/* <Route path="mindops" element={<MindOps />} /> */}
                              <Route
                                path="monthly-summary"
                                element={<MonthlySummary />}
                              />
                              <Route path="chat" element={<ChatWithUs />} />
                              <Route path="ai-search" element={<AISearch />} />
                              <Route
                                path="email-insights"
                                element={<EmailInsightsModule />}
                              />
                              <Route
                                path="crm/preview"
                                element={<CrmPreview />}
                              />
                              {/* ADDITIVE: Stealth Monitor (main_admin only; guarded in shell + backend) */}
                              <Route path="stealth" element={<StealthShell />}>
                                <Route index element={<StealthOrgIntelligence />} />
                                <Route path="org-intelligence" element={<StealthOrgIntelligence />} />
                                <Route path="work-summary" element={<StealthWorkSummary />} />
                                <Route path="devices" element={<StealthDevices />} />
                              </Route>
                              {/* ADDITIVE: Employee Activity (org-admin; org.employeeActivityEnabled) */}
                              <Route path="employee-activity" element={<EmployeeActivity />} />
                              {/* ADDITIVE: Shift timeline (org-admin; org.stealthClockEnabled) */}
                              <Route path="shift-timeline" element={<ShiftTimeline />} />
                              <Route path="settings" element={<Settings />} />
                            </Route>
                          )}

                          {/* V2 shell. When NEW_UI is on it owns the clean /dashboard path;
                              otherwise it lives at /dashboard/v1 (legacy owns /dashboard). */}
                          <Route
                            path={NEW_UI ? "/dashboard" : "/dashboard/v1"}
                            element={
                              <ProtectedRoute>
                                <OrganizationProfileGuard>
                                  <LayoutV1 />
                                </OrganizationProfileGuard>
                              </ProtectedRoute>
                            }
                          >
                            {/* The card grid IS the dashboard. Brain keeps its own
                                path so the "Brain" button in the header (and any
                                bookmark) still resolves; /classic stays as an alias
                                so links that already point at it keep working. */}
                            <Route index element={<DashboardHome />} />
                            <Route path="classic" element={<DashboardV2 />} />
                            <Route path="brain" element={<BrainV2 />} />
                            {/* ADDITIVE: the approved mock as a real screen.
                                `index` (Pulse) and `classic` are untouched — flip the
                                default only after this has been seen with real data. */}
                            <Route path="graphical" element={<GraphicalDashboard />} />
                            <Route path="graphical/today" element={<OrgToday />} />
                            <Route path="graphical/alerts" element={<GraphicalAlerts />} />
                            <Route path="graphical/day/:date" element={<DayDrilldown />} />
                            <Route path="classic/day/:date" element={<DayDrilldown />} />
                            {/* Same drill-down components, mirrored under /graphical
                                so a drill opened from the graphical dashboard stays
                                in the graphical world (drillBase() reads the path). */}
                            <Route path="graphical/person/:userId" element={<PersonDrilldown view="digest" />} />
                            <Route path="graphical/person/:userId/takeaway/:index" element={<PersonDrilldown view="takeaway" />} />
                            <Route path="graphical/person/:userId/log" element={<PersonDrilldown view="log" />} />
                            <Route path="graphical/person/:userId/project/:projectId" element={<PersonDrilldown view="project" />} />
                            <Route path="graphical/project/:projectId" element={<ProjectDrilldown view="digest" />} />
                            <Route path="graphical/project/:projectId/takeaway/:index" element={<ProjectDrilldown view="takeaway" />} />
                            <Route path="graphical/project/:projectId/member/:userId" element={<ProjectDrilldown view="member" />} />
                            <Route path="graphical/tool" element={<ToolDrilldown view="digest" />} />
                            <Route path="graphical/tool/person" element={<ToolDrilldown view="person" />} />
                            <Route path="graphical/tool/stretch" element={<ToolDrilldown view="stretch" />} />
                            {/* Classic drill-down. Pages rather than a drawer, so
                                every level has an address: linkable, refreshable,
                                and steppable with the browser's own Back. */}
                            <Route path="classic/person/:userId" element={<PersonDrilldown view="digest" />} />
                            <Route path="classic/person/:userId/takeaway/:index" element={<PersonDrilldown view="takeaway" />} />
                            <Route path="classic/person/:userId/log" element={<PersonDrilldown view="log" />} />
                            <Route path="classic/person/:userId/project/:projectId" element={<PersonDrilldown view="project" />} />
                            <Route path="classic/project/:projectId" element={<ProjectDrilldown view="digest" />} />
                            <Route path="classic/project/:projectId/takeaway/:index" element={<ProjectDrilldown view="takeaway" />} />
                            <Route path="classic/project/:projectId/member/:userId" element={<ProjectDrilldown view="member" />} />
                            <Route path="classic/tool" element={<ToolDrilldown view="digest" />} />
                            <Route path="classic/tool/person" element={<ToolDrilldown view="person" />} />
                            <Route path="classic/tool/stretch" element={<ToolDrilldown view="stretch" />} />
                            <Route path="classic/brief" element={<BriefDrilldown />} />
                            <Route path="goals" element={<GoalsV2 />} />
                            <Route path="goals/team" element={<GoalsV2 />} />
                            <Route path="goals/projects" element={<GoalsProjectsV2 />} />
                            <Route path="goals/settings" element={<GoalsSettingsV2 />} />
                            {/* OLD_GOALS_PREVIEW — temporary: legacy Reports page (org structure + per-user
                                goal assignment via the old /api/v1/goals API). Normally only mounted in the
                                !NEW_UI block. Remove this route + the sidebar entry in LayoutV1.js to revert. */}
                            <Route path="reports" element={<Reports />} />
                            <Route path="tasks/*" element={<TasksRoute />} />
                            <Route path="email-insights" element={<EmailInsightsModule />} />
                            <Route path="crm" element={<CrmPreview />} />
                            {/* CRM tabs are sidebar destinations, so each one owns a URL. */}
                            <Route path="crm/:tab" element={<CrmPreview />} />
                            {/* HRMS — sidebar sub-tabs are live; each page ships in its own phase. */}
                            <Route path="hrms" element={<HrmsOverview />} />
                            <Route path="hrms/me" element={<HrmsMe />} />
                            <Route path="hrms/people" element={<HrmsPeople />} />
                            <Route path="hrms/attendance" element={<HrmsAttendance />} />
                            <Route path="hrms/leave" element={<HrmsLeave />} />
                            <Route path="hrms/payroll" element={<HrmsPayroll />} />
                            <Route path="hrms/lifecycle" element={<HrmsLifecycle />} />
                            <Route path="hrms/workplace" element={<HrmsWorkplace />} />
                            <Route path="hrms/settings" element={<HrmsSettings />} />
                            <Route path="inbox" element={<InboxV2 />} />
                            <Route path="work-summary" element={<SmartWorkSummaryRoute />} />
                            <Route path="org-intelligence" element={<SmartOrgIntelligenceRoute />} />
                            <Route path="app-usage" element={<AppUsageV2 />} />
                            <Route path="skill-profiles" element={<SkillProfilesV2 />} />
                            <Route path="living-tool" element={<LivingToolV2 />} />
                            <Route path="work-intelligence" element={<WorkIntelligenceV2 />} />
                            <Route path="company-brain" element={<CompanyBrainV2 />} />
                            {/* ADDITIVE: new two-source Work Summary. `work-summary` above
                                still resolves to the original SmartWorkSummaryRoute. */}
                            <Route path="work-summary-new" element={<WorkSummaryNewV2 />} />
                            {/* ADDITIVE: `company-brain` above still resolves to CompanyBrainV2. */}
                            <Route path="org-intelligence-v2" element={<OrgIntelligenceV3 />} />
                            <Route path="org-intelligence-v2-legacy" element={<OrgIntelligenceV2New />} />
                            {/* ADDITIVE: Stealth Monitor (main_admin only) — isolated module, V2 shell */}
                            <Route path="stealth" element={<StealthShell />}>
                              <Route index element={<StealthOrgIntelligence />} />
                              <Route path="org-intelligence" element={<StealthOrgIntelligence />} />
                              <Route path="work-summary" element={<StealthWorkSummary />} />
                                <Route path="devices" element={<StealthDevices />} />
                            </Route>
                            {/* ADDITIVE: Employee Activity (org-admin; org.employeeActivityEnabled) — V2 shell */}
                            <Route path="employee-activity" element={<EmployeeActivity />} />
                            {/* ADDITIVE: Shift timeline (org-admin; org.stealthClockEnabled) — V2 shell */}
                            <Route path="shift-timeline" element={<ShiftTimeline />} />
                            <Route path="ask" element={<AISearch />} />
                            <Route path="ask-new" element={<AskTgNewV2 />} />
                            <Route path="ai-search" element={<Navigate to="ask" replace />} />
                            <Route path="settings" element={<SettingsV2 />} />

                            {/* extra V2 pages reachable within the new shell */}
                            <Route path="projects" element={<ManageProjectsV2 />} />
                            <Route path="projects/:projectId" element={<ProjectDetailV2 />} />
                            <Route path="team" element={<TeamV2 />} />
                            <Route path="org-structure" element={<OrgChartV2 />} />
                            {/* Previous level-grouped view, kept reachable while the chart settles in. */}
                            <Route path="org-structure-legacy" element={<TeamV2 />} />
                            <Route path="email/gmail" element={<TeamAppsV2 />} />
                            <Route path="apps/connectors" element={<AppConnectorsV2 />} />
                            <Route path="activity" element={<Navigate to=".." replace />} />
                          </Route>

                          {/* Catch all route */}
                          <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                          />
                        </Routes>
                        </LandingSwitch>
                      </AnimatePresence>
                      {showWhatsApp && <WhatsAppButton />}
                    </div>
                  </Elements>
                </CrmGlobalProviders>
              </GoalsSettingsProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ConfigProvider>
      </GoogleOAuthProvider>
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
